import { useState } from "react";
import axios from "axios";
import { useViaCep } from "@/lib/viacep";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export const emptyProfile = {
  doc_type: "pj", document: "", legal_name: "", trading_name: "", cep: "", street: "", number: "", complement: "",
  neighborhood: "", city: "", uf: "", responsible_name: "", responsible_phone: "", email: "", whatsapp: "", internal_notes: "",
  plan: "", amount: "", due_day: "", start_date: "",
};

export function profileFromMeta(meta) {
  const p = meta?.profile || {};
  const b = meta?.billing || {};
  const out = { ...emptyProfile };
  Object.keys(emptyProfile).forEach((k) => { if (p[k] != null) out[k] = p[k]; });
  return { ...out, doc_type: p.doc_type || "pj", plan: b.plan || "", amount: b.amount ?? "", due_day: b.due_day ?? "", start_date: b.start_date || "" };
}

export function profilePayload(f) {
  return { ...f, amount: f.amount === "" ? null : Number(String(f.amount).replace(",", ".")), due_day: f.due_day === "" ? null : Number(f.due_day), start_date: f.start_date || null };
}

const cls = "mt-1 bg-zinc-900 border-zinc-800";

function F({ label, k, form, set, className = "", ...rest }) {
  return (
    <div className={className}>
      <Label className="text-xs font-mono-caps text-zinc-400">{label}</Label>
      <Input data-testid={`shop-profile-${k}`} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={cls} {...rest} />
    </div>
  );
}

function DocLookup({ form, set, onFill }) {
  const [busy, setBusy] = useState(false);
  const digits = (form.document || "").replace(/\D/g, "");
  const lookup = async () => {
    if (form.doc_type !== "pj" || digits.length !== 14) { toast.info("Busca automática só para CNPJ com 14 dígitos"); return; }
    setBusy(true);
    try {
      const { data: d } = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${digits}`, { timeout: 8000, withCredentials: false });
      onFill({
        legal_name: d.razao_social || "", trading_name: d.nome_fantasia || "", cep: d.cep || "", street: `${d.descricao_tipo_de_logradouro || ""} ${d.logradouro || ""}`.trim(),
        number: d.numero || "", complement: d.complemento || "", neighborhood: d.bairro || "", city: d.municipio || "", uf: d.uf || "",
        email: (d.email || "").toLowerCase(), responsible_phone: form.responsible_phone || (d.ddd_telefone_1 || "").replace(/\D/g, ""),
      });
      toast.success("Dados da Receita preenchidos");
    } catch { toast.error("CNPJ não encontrado na Receita"); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-[110px_1fr_auto] gap-2 items-end">
      <div>
        <Label className="text-xs font-mono-caps text-zinc-400">Tipo</Label>
        <select data-testid="shop-profile-doc_type" value={form.doc_type} onChange={(e) => set("doc_type", e.target.value)}
                className="mt-1 h-10 w-full rounded-md bg-zinc-900 border border-zinc-800 px-2 text-sm text-zinc-100">
          <option value="pj">CNPJ</option><option value="pf">CPF</option>
        </select>
      </div>
      <F label={form.doc_type === "pj" ? "CNPJ" : "CPF"} k="document" form={form} set={set} inputMode="numeric"
         onBlur={() => { if (form.doc_type === "pj" && digits.length === 14 && !form.legal_name) lookup(); }} />
      <button type="button" data-testid="shop-profile-lookup" onClick={lookup} disabled={busy || form.doc_type !== "pj"}
              className="h-10 px-3 rounded-md border border-zinc-700 text-zinc-200 text-xs font-bold uppercase disabled:opacity-40 inline-flex items-center gap-1">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Receita
      </button>
    </div>
  );
}

export function ShopProfileFields({ form, setForm, addons = [] }) {
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onFill = (vals) => setForm((p) => ({ ...p, ...vals }));
  useViaCep(form?.cep, (d) => setForm((p) => ({
    ...p,
    street: d.logradouro || p.street,
    neighborhood: d.bairro || p.neighborhood,
    city: d.localidade || p.city,
    uf: (d.uf || "").toUpperCase() || p.uf,
  })));
  const box = "space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4";
  return (
    <div className="space-y-3" data-testid="shop-profile-fields">
      <fieldset className={box}>
        <legend className="text-[11px] font-mono-caps text-zinc-500 px-2">Cadastro (Receita)</legend>
        <DocLookup form={form} set={set} onFill={onFill} />
        {form.doc_type === "pf" && <p className="text-[11px] text-zinc-500">A Receita não oferece consulta pública de CPF — preencha manualmente.</p>}
        <F label={form.doc_type === "pj" ? "Razão social" : "Nome completo"} k="legal_name" form={form} set={set} />
        <F label="Nome fantasia" k="trading_name" form={form} set={set} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <F label="CEP" k="cep" form={form} set={set} inputMode="numeric" />
          <F label="Rua" k="street" form={form} set={set} className="col-span-2 sm:col-span-3" />
          <F label="Número" k="number" form={form} set={set} />
          <F label="Complemento" k="complement" form={form} set={set} />
          <F label="Bairro" k="neighborhood" form={form} set={set} className="col-span-2" />
          <F label="Cidade" k="city" form={form} set={set} className="col-span-2 sm:col-span-3" />
          <F label="UF" k="uf" form={form} set={set} maxLength={2} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <F label="Responsável" k="responsible_name" form={form} set={set} />
          <F label="Telefone do responsável" k="responsible_phone" form={form} set={set} inputMode="tel" />
          <F label="E-mail da empresa" k="email" form={form} set={set} type="email" />
          <F label="WhatsApp da empresa" k="whatsapp" form={form} set={set} inputMode="tel" />
        </div>
      </fieldset>
      <fieldset className={box}>
        <legend className="text-[11px] font-mono-caps text-zinc-500 px-2">Contrato</legend>
        <div className="grid grid-cols-2 gap-2">
          <F label="Plano" k="plan" form={form} set={set} placeholder="Ex.: Completo" />
          <F label="Mensalidade (R$)" k="amount" form={form} set={set} inputMode="decimal" />
          <F label="Dia de vencimento" k="due_day" form={form} set={set} type="number" min={1} max={28} />
          <F label="Data de início" k="start_date" form={form} set={set} type="date" />
        </div>
        <div data-testid="shop-profile-addons" className="text-xs text-zinc-400">
          Adicionais contratados: {addons.length ? addons.join(", ") : "nenhum"} <span className="text-zinc-600">(gerencie em Pacotes adicionais)</span>
        </div>
      </fieldset>
      <fieldset className="space-y-2 rounded-xl border border-amber-900/50 bg-amber-950/10 p-4">
        <legend className="text-[11px] font-mono-caps text-amber-400/80 px-2">Observações internas (só super admin)</legend>
        <Textarea data-testid="shop-profile-internal_notes" value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)}
                  rows={3} className="bg-zinc-900 border-zinc-800" placeholder="Anotações que a oficina nunca vê" />
      </fieldset>
    </div>
  );
}
